import * as React from 'react';
import onClickOutside from 'react-onclickoutside';
import classnames from 'classnames';

import Button, { DeleteButton } from '@components/Button';
import Checkbox from '@components/Checkbox';
import Input from '@components/Input';

import { getBoundingClientRect } from '@utils/WindowUtils';

import './style.scss';

export interface IDropdownProps {
  id: string;
  name: string;
  label: string;
  value: any;
  options: Array<{
    value: any;
    label: string;
    isDisabled?: boolean;
    icon?: any;
    allowDelete?: boolean;
    isActive?: boolean;
  }>;
  selectedOptions?: Array<{ value: any; label: string; icon?: any; isActive?: boolean }>;
  onChange: TButtonOnClick;
  onBlur?: TButtonOnClick;
  calledFromContacts?: boolean;
  onFocus?: TButtonOnClick;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multi?: boolean;
  skipRefetch?: boolean;
  hasSelectAll?: boolean;
  onSelectAll?: () => void;
  isAllSelected?: boolean;
  offset?: number;
  error?: IFieldValidation;
  width?: string;
  title?: string;
  searchMode?: boolean;
  searchPlaceholder?: string;
  shouldHighlightMatchingSubstring?: boolean;
  onLoadMoreData?: (params: ILoadMoreParams) => Promise<void>;
  lazyLoad?: boolean;
  hasSubmitSection?: boolean;
  submitSectionLabel?: string;
  onSubmit?: any;
  onCancel?: any;
  className?: string;
  type?: DropdownType;
  onDelete?: (index: number) => void;
  showHeader?: any;
  shouldLoadMoreData?: boolean;
}

interface IDropdownState {
  show: boolean;
  query: string;
  isLoading: boolean;
}

export class Dropdown extends React.Component<IDropdownProps, IDropdownState> {
  public static defaultProps = {
    name: '',
    value: '',
    label: '',
    clearable: false,
    onChange: null,
    placeholder: 'Select Option',
    options: null,
    disabled: false,
    hasSelectAll: false,
    multi: false,
    searchMode: false,
    shouldHighlightMatchingSubstring: true,
    type: DropdownType.NO_BORDER,
    count: null,
    onLoadMoreData: (params: ILoadMoreParams) => null,
    shouldLoadMoreData: true,
  };

  constructor(props: IDropdownProps) {
    super(props);
    this.state = this.getInitState();
  }

  getInitState = () => ({
    show: false,
    query: '',
    isLoading: false,
  });

  toggleDropdown = (e?: React.SyntheticEvent<any>) => {
    if (e) {
      e.preventDefault();
    }

    this.setState((prevState) => ({
      show: !this.props.disabled && !prevState.show,
      query: '',
    }));
  };

  onClose = (e?: React.SyntheticEvent<any>) => {
    this.setState({
      show: false,
    });
  };

  onCancel = (e?: React.SyntheticEvent<any>) => {
    this.onClose();
    this.props.onCancel();
  };

  onSubmit = (e?: React.SyntheticEvent<any>) => {
    this.onClose();
    this.props.onSubmit();
  };

  onDelete = (index: number) => (e: React.SyntheticEvent<any>) => {
    e.stopPropagation();
    e.preventDefault();
    this.props.onDelete(index);
  };

  handleClickOutside = (e: React.SyntheticEvent<any>) => {
    if (this.props.lazyLoad && this.state.show && this.props.shouldLoadMoreData && !this.props.calledFromContacts) {
      const limit = 20;
      const offset = 0;
      this.props.onLoadMoreData({
        query: '',
        type: this.props.id,
        limit,
        offset,
      });
    }
    if (this.props.onCancel && this.state.show) {
      this.props.onCancel();
    }
    this.setState({ show: false, query: '' });
  };

  onChange = (index: number) => (e: React.SyntheticEvent<any>) => {
    const value = this.props.options[index] ? this.props.options[index].value : '';
    if (this.props.multi) {
      const eventObj = {
        name: this.props.name,
        value: !this.props.value.includes(value)
          ? this.props.value.concat(value)
          : this.props.value.filter((elem) => elem !== value),
      };
      this.props.onChange({
        target: eventObj,
        currentTarget: eventObj,
      });
      if (this.props.onLoadMoreData && !this.props.skipRefetch) {
        const limit = 20;
        const offset = this.props.shouldLoadMoreData ? 0 : this.props.offset ?? this.props.options.length;
        this.props.onLoadMoreData({
          query: this.state.query,
          type: this.props.id,
          limit,
          offset,
        });
      }
    } else {
      this.props.onChange({
        target: { name: this.props.name, value },
        currentTarget: { name: this.props.name, value },
      });
    }
    if (!this.props.multi) {
      this.onClose();
    }
  };

  onSelectedChange = (index: number) => (e: React.SyntheticEvent<any>) => {
    const value = this.props.selectedOptions[index] ? this.props.selectedOptions[index].value : '';
    const eventObj = {
      name: this.props.name,
      value: this.props.value.filter((elem) => elem !== value),
    };
    this.props.onChange({
      target: eventObj,
      currentTarget: eventObj,
    });
    if (this.props.shouldLoadMoreData) {
      const limit = 20;
      const offset = 0;
      this.props.onLoadMoreData({
        query: this.state.query,
        type: this.props.id,
        limit,
        offset,
      });
    }
  };

  renderSelector = () => {
    const { options, selectedOptions, value, label, placeholder, multi, id, disabled, required } = this.props;

    const option = options?.find((elem) => elem.value === value);
    const filterLabel = !multi
      ? option
        ? option.label
        : ''
      : value
          .reduce((acc, curr) => {
            const existingObj = this.props.lazyLoad
              ? selectedOptions.find((elem) => elem.value === curr)
              : options.find((elem) => elem.value === curr);

            if (existingObj) {
              acc.push(`${existingObj.label} ${existingObj?.isActive === false ? '(Deactivated)' : ''}`);
            }

            return acc;
          }, []) // tslint:disable-line
          .join(', ');

    return (
      <div className={classnames('dropdown__selector', { disabled, required })} onClick={this.toggleDropdown}>
        {label && (
          <label htmlFor={id} className={classnames('dropdown__selector__label')}>
            {label}
          </label>
        )}
        <div onClick={this.onClearSearchQuery} className={classnames('dropdown__selector__selected')}>
          <label>{filterLabel || placeholder}</label>
          <span className={classnames('dropdown__arrow')} />
        </div>
      </div>
    );
  };

  onClearSearchQuery = (e?: React.SyntheticEvent<any>) => {
    const query = '';
    this.setState({ query }, () => {
      if (this.props.lazyLoad) {
        const limit = 20;
        const offset = this.props.shouldLoadMoreData ? 0 : this.props.offset ?? this.props.options.length;
        this.props.onLoadMoreData({
          query,
          type: this.props.id,
          limit,
          offset,
        });
      } else {
        this.props.onLoadMoreData({
          query,
          type: this.props.id,
          limit: null,
          offset: null,
        });
      }
    });
  };

  onSearchQueryChange = (e: React.SyntheticEvent<any>) => {
    const query = e.currentTarget.value;
    this.setState({ query }, () => {
      if (this.props.lazyLoad) {
        const limit = 20;
        const offset = this.props.shouldLoadMoreData ? 0 : this.props.offset ?? this.props.options.length;
        this.props.onLoadMoreData({
          query: query.trim(),
          type: this.props.id,
          limit,
          offset,
        });
      } else {
        this.props.onLoadMoreData({
          query: query.trim(),
          type: this.props.id,
          limit: null,
          offset: null,
        });
      }
    });
  };

  handleContainerOnBottom = (e: any) => {
    if (this.props.lazyLoad) {
      const element = e.target;
      const offsetheight = 40;
      const limit = 20;
      if (
        element.scrollHeight - element.scrollTop <= element.clientHeight + offsetheight &&
        this.state.show &&
        !this.state.isLoading
      ) {
        this.setState({
          isLoading: true,
        });
        this.props
          .onLoadMoreData({
            offset: this.props.offset ? this.props.offset : this.props.options.length,
            type: this.props.id,
            limit,
          })
          .then(() => {
            this.setState({
              isLoading: false,
            });
          });
      }
    }
  };

  getHighlightedSubstring = (label: string) => {
    const { query } = this.state;
    const { shouldHighlightMatchingSubstring } = this.props;
    let suggestionLabel = label;
    if (shouldHighlightMatchingSubstring) {
      const escape = (str: string) => str.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
      const queryRegex = new RegExp(escape(query.trim()), 'ig');
      suggestionLabel = suggestionLabel.replace(queryRegex, '<b>$&</b>');
    }

    return suggestionLabel;
  };

  renderDropdownActions = () => (
    <div className={classnames('flex-row--center', 'dropdown__actions')}>
      <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onCancel} content="Cancel" />
      <Button
        className={classnames('space-left')}
        bsStyle={ButtonStyle.PRIMARY}
        onClick={this.onSubmit}
        content={this.props.submitSectionLabel || 'Apply'}
      />
    </div>
  );

  renderDropdown = () => {
    const {
      options,
      multi,
      value,
      title,
      id,
      searchMode,
      selectedOptions,
      hasSubmitSection,
      hasSelectAll,
      isAllSelected,
    } = this.props;
    const { query } = this.state;
    const isSelected = (option) => (multi ? value.find((e) => e === option.value) : option.value === value);
    const hideOption = (option) => {
      const query = this.state.query?.trim();

      return query && !option.label.toLowerCase().includes(query.toLowerCase());
    };

    return (
      <div id={id} className={classnames('dropdown__list')}>
        {this.props.showHeader ? this.props.showHeader() : ''}
        {searchMode && (
          <div
            className={classnames('search', {
              'search--searching': query.trim(),
            })}
          >
            <Input
              field={{
                label: '',
                type: CustomFieldType.TEXT,
                isRequired: false,
                value: this.state.query,
              }}
              placeholder={this.props.searchPlaceholder}
              width={12}
              name="query"
              onChange={this.onSearchQueryChange}
              className={classnames('search__input')}
            />
            <span className={classnames('search__img')} onClick={this.onClearSearchQuery} />
          </div>
        )}

        <ul
          onScroll={this.handleContainerOnBottom}
          className={classnames('dropdown__options--single')}
          tabIndex={0}
        >
          {multi && hasSelectAll && (
            <li className="picklist-with-handler__top-handler multi">
              <Checkbox id="all" isChecked={isAllSelected} name="All" onChange={this.props.onSelectAll}>
                All
              </Checkbox>
            </li>
          )}
          {selectedOptions && selectedOptions.length > 0 && (
            <div className={classnames('selected__options')}>
              {selectedOptions.map((option, index) => (
                <li className={classnames({ selected: isSelected(option) })} key={index}>
                  <Checkbox
                    id={`${this.props.label}-selected-${index}`}
                    onChange={this.onSelectedChange(index)}
                    isChecked={true}
                    name={option.label}
                    className="checkbox-holder--radio"
                  >
                    {searchMode ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: this.getHighlightedSubstring(option.label),
                        }}
                      />
                    ) : (
                      option.label
                    )}

                    {option.icon}
                  </Checkbox>
                </li>
              ))}
            </div>
          )}
          {title && <li className={classnames('title')}>{title}</li>}
          {options &&
            options.map((option, index) => (
              <li
                className={classnames('option', {
                  selected: isSelected(option),
                  hide: hideOption(option),
                  disabled: option.isDisabled,
                  active: option?.isActive === false,
                })}
                key={index}
                onClick={!multi && !option.isDisabled ? this.onChange(index) : undefined}
              >
                {multi ? (
                  <Checkbox
                    id={`${this.props.label}-${index}`}
                    onChange={!option.isDisabled ? this.onChange(index) : undefined}
                    isChecked={this.props.value.includes(option.value)}
                    name={option.label}
                    className="checkbox-holder--radio"
                  >
                    {searchMode ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: this.getHighlightedSubstring(option.label),
                        }}
                      />
                    ) : (
                      option.label
                    )}
                    {option?.isActive === false ? (
                      <span className={classnames('option-active')}>&nbsp; (Deactivated)</span>
                    ) : null}
                    {option.icon}
                  </Checkbox>
                ) : (
                  <>
                    {searchMode ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: this.getHighlightedSubstring(option.label),
                        }}
                      />
                    ) : (
                      option.label
                    )}
                    {option?.isActive === false ? (
                      <span className={classnames('option-active')}>&nbsp; (Deactivated)</span>
                    ) : null}
                  </>
                )}
                {this.props.onDelete && option.allowDelete && <DeleteButton onClick={this.onDelete(index)} />}
              </li>
            ))}
        </ul>
        {hasSubmitSection ? this.renderDropdownActions() : null}
      </div>
    );
  };

  render() {
    const { width, className, value, error, type } = this.props;
    const boundingClientRect = getBoundingClientRect('.dropdown__wrapper');
    const style = boundingClientRect
      ? {
          maxWidth: width || boundingClientRect.width,
        }
      : null;

    const optionSelected = Array.isArray(value) ? value.length : value;

    return (
      <div
        style={style}
        className={classnames(`dropdown__wrapper ${className ? className : ''}`, {
          'dropdown__wrapper--open': this.state.show,
          'dropdown__wrapper--box': type === DropdownType.BORDER,
          'option-selected': optionSelected,
          'dropdown--error':
            error && (error.errorState === IValidationState.ERROR || error.errorState === IValidationState.WARNING),
        })}
      >
        {this.renderSelector()}
        {error && (
          <span className="field__error">
            {error.errorState === IValidationState.ERROR || error.errorState === IValidationState.WARNING
              ? error.errorMessage
              : ''}
          </span>
        )}
        {this.renderDropdown()}
      </div>
    );
  }
}

export default onClickOutside(Dropdown);
